/* @flow */
import * as React from 'react';
import { observable } from 'mobx';
import { observer, inject } from 'mobx-react';
import type { TranslatorProps } from 'react-i18next';
import { translate } from 'react-i18next';
import RequireForAccess, { ReactAccessConsumer } from 'react-access';
import Paper from '@material-ui/core/Paper';

import UserStore from 'stores/UserStore';
import AuthenticationStore from 'stores/AuthenticationStore';
import ElectroResponseMessage from 'forms/components/inputs/ElectroResponseMessage';
import Activity from 'components/Activity/Activity';
import UserProfileRole from 'components/User/UserProfileRole';
import activityStyles from 'common-scss/DetailsPage.module.scss';
import {
  autoCompleteCountries,
  autoCompleteCurrencies,
  autoCompletePriceFormats,
  autoCompleteTradingAccounts,
  autoCompleteRegions,
  autoCompleteUSStates,
} from 'api/forms.js';
import { userManagement } from 'permissions/general';
import { updateRegion, viewRegion } from 'permissions/account';
import { Roles } from 'permissions/defs';
import { customerUsers, internalUsers } from 'permissions/userTypes';
import { ComponentWithInjectedProps } from 'utils/injectedProps';
import { ENV } from 'config';

import AutoCompleteModel from '../../models/forms/AutoCompleteModel';
import { getForm } from './setupSelectedUserProfile';
import PageWrapper from '../../components/PageWrapper/PageWrapper';
import FormWrapper from 'components/FormWrapper/FormWrapper';
import FormSection from '../../components/FormSection/FormSection';
import ElectroSelectExtra from '../../forms/components/inputs/ElectroSelectExtra/ElectroSelectExtra';
import ElectroTextField from '../../forms/components/inputs/ElectroTextField/ElectroTextField';
import ElectroCheckbox from '../../forms/components/inputs/ElectroCheckbox/ElectroCheckbox';
import ElectroSwitch from '../../forms/components/inputs/ElectroSwitch';
import ElectroToggledRadioGroup from '../../forms/components/inputs/ElectroToggledRadioGroup/ElectroToggledRadioGroup';
import ElectroSelect from '../../forms/components/inputs/ElectroSelect/ElectroSelect';
import FormControls from '../../forms/components/controls/FormControls/FormControls';
import Loading from '../../components/Loading/Loading';
import { generateUrl, user } from 'api/routes';

export type SelectedUserProfileProps = {
  form: Object,
  selectedUserId?: string,
  mode: string,
};

type SelectedUserProfileInjectedProps = {
  userStore: UserStore,
} & TranslatorProps;

@translate()
@inject('userStore')
@observer
class SelectedUserProfile extends ComponentWithInjectedProps<
  SelectedUserProfileProps,
  {},
  SelectedUserProfileInjectedProps
> {
  @observable localState = {
    selectedValue: '',
    selectedAccounts: '',
    showSaveBtn: true,
  };
  @observable accountsModel: AutoCompleteModel = new AutoCompleteModel(autoCompleteTradingAccounts);
  @observable locationCountry: AutoCompleteModel = new AutoCompleteModel(autoCompleteCountries);
  @observable locationState: AutoCompleteModel = new AutoCompleteModel(autoCompleteUSStates);
  @observable currenciesModel: AutoCompleteModel = new AutoCompleteModel(autoCompleteCurrencies);
  @observable priceFormatsModel: AutoCompleteModel = new AutoCompleteModel(
    autoCompletePriceFormats
  );
  @observable regionsModel: AutoCompleteModel = new AutoCompleteModel(autoCompleteRegions);
  @observable selectedCountry = '';

  componentDidMount() {
    const form = this.props.form;
    this.localState.selectedAccounts = form.$('assignOtherAccounts.accounts').value;
  }

  componentDidUpdate() {
    const { form } = this.props;

    const accountsModel = this.accountsModel;
    const showRegion = this.regionAccessByRoleName(form.$('selectUserRole.userRole').value);
    const regionID = form.$('userProfile.region').extra.selectID;

    if (showRegion) {
      accountsModel.filterId = regionID;
      accountsModel.loadData();
    } else {
      accountsModel.filterId = '';
      accountsModel.loadData();
    }
  }

  handleOnChange = (model: Object, selectedValue: Object) => {
    model.setResult(selectedValue);
    this.selectedCountry = selectedValue.code;
  };

  setTradingAccount = (accounts: string) => {
    this.localState.selectedAccounts = accounts;
  };

  resetRegionAccounts() {
    const resetFields = [
      // Extra Fields
      this.props.form.$('assignOtherAccounts.accounts'),
    ];

    // Reset Fields
    for (let i: * = 0; i < resetFields.length; i++) {
      resetFields[i].value = '';
      resetFields[i].extra.selectID = '';
    }

    // Reset values on projects
    this.localState.selectedAccounts = '';
  }

  regionAccessByRoleName(roleName: string | number) {
    switch (Number(roleName)) {
      case Roles.USER:
      case Roles.MANAGER:
      case Roles.PURCHASER:
      case Roles.VIEW_ONLY:
      case Roles.ACCOUNT_ADMIN:
        return false;
      case Roles.ACCOUNT_MANAGER:
      case Roles.ASSET_MANAGER:
      case Roles.BUSINESS_VIEW_ONLY:
      case Roles.SYSTEM_ADMIN:
      case Roles.SYSTEM_ADMIN_REGIONAL:
        return true;
      default:
        return false;
    }
  }

  renderActivities() {
    const { userStore } = this.props;

    return (
      <Paper square={true} className={activityStyles['activity-panel']}>
        {userStore.selectedUser && (
          <Activity
            title={true}
            url={generateUrl(user.activity, [userStore.selectedUser.id])}
            allowFilter={true}
            refresh={userStore.selectedUser}
          />
        )}
      </Paper>
    );
  }

  renderFormFields() {
    const { form, userStore, mode, t } = this.props;
    const {
      locationCountry,
      locationState,
      currenciesModel,
      priceFormatsModel,
      accountsModel,
      regionsModel,
      selectedCountry,
    } = this;
    const userRoleId = userStore.selectedUser ? userStore.selectedUser.role_id : undefined;
    const showRegion = this.regionAccessByRoleName(form.$('selectUserRole.userRole').value);
    const disabled = mode === 'edit';
    const showSaveBtn = mode === 'edit' ? true : this.localState.showSaveBtn;

    return (
      <React.Fragment>
        <FormSection form={form} section="userProfile">
          {disabled && <ElectroTextField field={form.$('userID.id')} disabled={disabled} />}
          <ElectroTextField field={form.$('userProfile.firstName')} disabled={disabled} />
          <ElectroTextField field={form.$('userProfile.lastName')} disabled={disabled} />
          <ElectroTextField
            field={form.$('userProfile.email')}
            disabled={disabled}
            mask={{ type: 'email', shouldMask: ENV === 'demo' }}
          />
          <ElectroTextField field={form.$('userProfile.phoneNumber')} disabled={disabled} />
          <ElectroSelect
            field={form.$('userProfile.country')}
            model={locationCountry}
            onChange={(selectedValue: Object) => {
              form.$('userProfile.country').value = selectedValue;
              this.handleOnChange(locationCountry, selectedValue);
            }}
            disabled={disabled}
          />
          {selectedCountry === 'US' && (
            <ElectroSelect
              field={form.$('userProfile.state')}
              model={locationState}
              onChange={(selectedValue: Object) => {
                form.$('userProfile.state').value = {
                  name: selectedValue.name,
                  abbreviation: selectedValue.code,
                };
              }}
              disabled={disabled}
            />
          )}
          {(selectedCountry === 'GB' || selectedCountry === 'CA') && (
            <ElectroTextField field={form.$('userProfile.county')} disabled={disabled} />
          )}
          <ElectroTextField field={form.$('userProfile.city')} disabled={disabled} />
          <ElectroTextField field={form.$('userProfile.postcode')} disabled={disabled} />
          {showRegion && (
            <RequireForAccess permissions={[...updateRegion, ...viewRegion]}>
              <ReactAccessConsumer>
                {authorizeAccess => {
                  if (regionsModel) {
                    return (
                      <ElectroSelectExtra
                        field={form.$('userProfile.region')}
                        disabled={!authorizeAccess(updateRegion)}
                        form={form}
                        formName="userProfile"
                        labelKey="name"
                        resetModels={() => this.resetRegionAccounts()}
                        props={{
                          model: regionsModel,
                          modelChild: accountsModel,
                        }}
                      />
                    );
                  }
                }}
              </ReactAccessConsumer>
            </RequireForAccess>
          )}
          <ElectroTextField field={form.$('userProfile.company')} disabled={disabled} />
        </FormSection>
        <FormSection form={form} section="displaySettings">
          {currenciesModel && (
            <ElectroSelect
              field={form.$('displaySettings.currency')}
              model={currenciesModel}
              onChange={(selectedValue: Object) => {
                form.$('displaySettings.currency').value = selectedValue;
              }}
              searchable={false}
            />
          )}
          {priceFormatsModel && (
            <ElectroSelect
              field={form.$('displaySettings.price')}
              model={priceFormatsModel}
              onChange={(selectedValue: Object) => {
                form.$('displaySettings.price').value = selectedValue;
              }}
              searchable={false}
              labelKey="format"
            />
          )}
        </FormSection>
        {mode === 'edit' && userRoleId < 6 && (
          <FormSection form={form} section="notificationSettings">
            <ElectroSwitch field={form.$('notificationSettings')} group={true} />
          </FormSection>
        )}
        <RequireForAccess permissions={userManagement}>
          <FormSection form={form} section="selectUserRole">
            <ElectroToggledRadioGroup
              field={form.$('selectUserRole.userRole')}
              secondaryKey="hint"
            />
          </FormSection>
          <FormSection form={form} section="assignOtherAccounts">
            <ElectroSelectExtra
              field={form.$('assignOtherAccounts.accounts')}
              form={form}
              formName="assignOtherAccounts"
              labelKey={[
                'name',
                'account_no',
                ...(form.$('assignOtherAccounts.accounts').extra.shouldShowCompanyCode
                  ? ['er_company_code']
                  : []),
              ]}
              multi={true}
              searchable={true}
              handleOnChange={this.setTradingAccount}
              singleSelectedValue={this.localState.selectedAccounts}
              labelSeparator=" - "
              props={{ model: accountsModel }}
            />
          </FormSection>
          {mode === 'edit' && (
            <FormSection form={form} section="accountStatus">
              <ElectroCheckbox field={form.$('accountStatus.disabled')} />
            </FormSection>
          )}
        </RequireForAccess>
        <FormControls
          form={form}
          showSaveBtn={showSaveBtn}
          buttonText={t('general:actions.invite')}
        />
      </React.Fragment>
    );
  }

  render() {
    const { form, userStore, mode }: Object = this.props;
    const userRole: number | null = userStore.selectedUser ? userStore.selectedUser.role_id : null;
    const userRoleId: ?number = userStore.selectedUser ? userStore.selectedUser.role_id : null;

    return mode === 'edit' ? (
      <div className={activityStyles['grid-wrapper']}>
        <Paper square={true} className={activityStyles['form-item']}>
          {userStore.selectedUser && (
            <UserProfileRole
              userDetails={userStore.selectedUser}
              userTypes={userRoleId && userRoleId > 5 ? internalUsers : customerUsers}
              userRole={userRole}
            />
          )}
          <div className={activityStyles['form-item-wrapper']}>{this.renderFormFields()}</div>
        </Paper>
        {this.renderActivities()}
      </div>
    ) : (
      <FormWrapper form={form}>{this.renderFormFields()}</FormWrapper>
    );
  }
}

type SelectedUserProfileWrapperInjectedProps = {
  userStore: UserStore,
  authenticationStore: AuthenticationStore,
} & TranslatorProps;

@translate()
@inject('authenticationStore', 'userStore')
@observer
class SelectedUserProfileWrapper extends ComponentWithInjectedProps<
  SelectedUserProfileProps,
  {},
  SelectedUserProfileWrapperInjectedProps
> {
  @observable localState = {
    responseMessage: '',
    showResponseMessage: false,
    isLoading: false,
    hasSuccess: false,
    hasError: false,
  };

  componentDidMount() {
    const { userStore, selectedUserId, mode } = this.props;

    mode === 'edit' && userStore.getSelectedUser(selectedUserId);
    userStore.getUserLocations();
    userStore.getUserLanguages();
    userStore.getUserCurrencies();
    userStore.getUserPriceFormats();
  }

  componentDidUpdate(prevProps: *) {
    const { userStore, selectedUserId } = this.props;

    if (prevProps.selectedUserId !== selectedUserId) {
      userStore.clearSelectedUser();
      userStore.getSelectedUser(selectedUserId);
    }
  }

  componentWillUnmount() {
    const { userStore } = this.props;
    userStore.clearSelectedUser();
  }

  render() {
    const { userStore, mode, t } = this.props;

    return userStore.selectedUser || mode !== 'edit' ? (
      <PageWrapper isFlex={false}>
        <ElectroResponseMessage localState={this.localState} />

        <SelectedUserProfile
          form={getForm(
            userStore.selectedUser,
            userStore,
            this.localState,
            t,
            mode,
            userStore.userRoleId
          )}
          mode={mode}
        />
      </PageWrapper>
    ) : (
      <Loading />
    );
  }
}

export default SelectedUserProfileWrapper;
